<template>
  <div>
    <button @click="toggleTableVisibility" class="button button-primary--small add-prescription-button" type="button">
      Update Order Set
    </button>

    <transition name="fade">
      <div v-if="notification.message" :class="['notification', notification.type]">
        {{ notification.message }}
        <button @click="clearNotification" class="close-notification">×</button>
      </div>
    </transition>

    <transition name="fade">
      <div v-if="showTable" class="overlay" @click="toggleTableVisibility">
        <div class="modal-content" @click.stop>
          <h2>Update Order Set</h2>
          <button @click="toggleTableVisibility" class="close-button" type="button">×</button>
          <div class="table-responsive">
            <table>
              <thead>
              <tr>
                <th>Product</th>
                <th>Quantity</th>
                <th>Sig</th>
                <th>Shipping date</th>
                <th>Price</th>
                <th>Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(prescription, idx) in localPrescriptions" :key="prescription.id">
                <td class="cell">
                  <select v-model="prescription.lineItem.product.id" class="product-select">
                    <template v-for="productVariant in productPrescriptionVariants">
                      <template v-if="productVariant.name === SSRIMedicationName">
                        <option v-for="defaultVariant in productPrescriptionVariantsDefault" :value="defaultVariant.id">
                          {{ defaultVariant.name }} {{ defaultVariant.strength }}
                        </option>
                      </template>
                      <option v-else :value="productVariant.id">
                        {{ productVariant.name }} {{ productVariant.strength }}
                      </option>
                    </template>
                  </select>
                </td>
                <td class="cell right-align">
                  <input v-model.number="prescription.lineItem.quantity" type="number" min="1" />
                </td>
                <td class="cell">
                  <input v-model="prescription.instructions" type="text" required />
                </td>
                <td class="cell">
                  <input
                      v-model="prescription.dateStart"
                      type="date"
                      v-if="idx === 0"
                      @change="updateSubsequentDates(0)"
                  />
                  <input
                      v-model="prescription.dateEnd"
                      type="date"
                      v-if="idx !== 0"
                      @change="updateSubsequentDates(idx)"
                  />
                </td>
                <td class="cell right-align">
                  <input v-model.number="prescription.lineItem.price" type="number" class="price-input" />
                </td>
                <td class="cell center">
                  <button @click="removePrescription(prescription.id)" class="remove-button" type="button">Remove</button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <div class="modal-footer">
            <button @click="addPrescription" class="button button-secondary--small" type="button">Add Row</button>
            <button
                @click="savePrescriptions"
                class="button button-primary--small"
                type="button"
                :disabled="isSaving"
            >
              <span v-if="isSaving">Saving...</span>
              <span v-else>Save</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    consultationId: { type: [Number, String], required: true },
    prescriptions: { type: Array, required: true },
    productPrescriptionVariants: { type: Array, required: true },
    productPrescriptionVariantsDefault: { type: Array, required: true },
  },
  data() {
    return {
      showTable: false,
      localPrescriptions: [],
      notification: {
        message: '',
        type: ''
      },
      isSaving: false,
      hasInitializedDates: false,
      sleepPrescriptionDayStep:30,
      SSRIMedicationName:'SSRI medication (Your psychiatrist will determine which medication may be right for you)'
    };
  },
  watch: {
    prescriptions: {
      immediate: true,
      handler(newVal) {
        this.localPrescriptions = JSON.parse(JSON.stringify(newVal));
        if (!this.hasInitializedDates) {
          this.initializeDates();
        }
      }
    }
  },
  methods: {
    toggleTableVisibility() {
      this.showTable = !this.showTable;
      if (this.showTable && !this.hasInitializedDates) {
        if (this.localPrescriptions.length === 0) {
          this.addPrescription();
        }
        this.localPrescriptions[0].dateStart = new Date().toISOString().split('T')[0];
        delete this.localPrescriptions[0].dateEnd;
        this.updateSubsequentDates(0);
        this.hasInitializedDates = true;
      } else if (!this.showTable) {
        this.localPrescriptions = JSON.parse(JSON.stringify(this.prescriptions));
        this.initializeDates();
      }
    },
    removePrescription(id) {
      const index = this.localPrescriptions.findIndex(p => p.id === id);
      if (index !== -1) {
        this.localPrescriptions.splice(index, 1);
        if (index === 0 && this.localPrescriptions.length > 0) {
          this.localPrescriptions[0].dateStart = this.localPrescriptions[0].dateEnd || new Date().toISOString().split('T')[0];
          delete this.localPrescriptions[0].dateEnd;
        }
        this.updateSubsequentDates(index === 0 ? 0 : index - 1);
      }
    },
    addPrescription() {
      if (this.productPrescriptionVariants.length === 0) {
        this.showNotification('No product variants available.', 'error');
        return;
      }
      const newId = -Date.now();
      const newPrescription = {
        id: newId,
        lineItem: {
          product: { id: this.productPrescriptionVariants[0].id },
          quantity: 90,
          strength: '10mg',
          price: 90
        },
        instructions: this.productPrescriptionVariants[0].info || '',
        ...(this.localPrescriptions.length === 0
            ? { dateStart: new Date().toISOString().split('T')[0] }
            : { dateEnd: this.calculateNextDate() })
      };
      this.localPrescriptions.push(newPrescription);
      if (this.localPrescriptions.length > 1) {
        this.updateSubsequentDates(this.localPrescriptions.length - 2);
      }
    },
    async savePrescriptions() {
      this.isSaving = true;
      try {
        const payload = {
          consultation_id: this.consultationId,
          prescriptions: this.localPrescriptions.map((prescription, index) => ({
            ...prescription,
            dateStart: index === 0 ? prescription.dateStart : undefined,
            dateEnd: index !== 0 ? prescription.dateEnd : undefined
          }))
        };

        const response = await axios.post('/dashboard/create-prescriptions', payload, {
          headers: { 'Content-Type': 'application/json' }
        });

        const { success, message, info } = response.data;
        const hasFailure = this.checkForFailures(info || {});

        if (success && !hasFailure) {
          this.showNotification(message || 'Prescriptions saved successfully!', 'success');
          this.toggleTableVisibility();
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          const errorMsg = this.extractErrorMessage(info, message);
          this.showNotification(errorMsg, 'error');
        }
      } catch (error) {
        const errorMsg = error.message || 'Failed to save prescriptions. Please try again.';
        this.showNotification(errorMsg, 'error');
      } finally {
        this.isSaving = false;
      }
    },
    initializeDates() {
      if (this.localPrescriptions.length === 0) return;
      if (!this.localPrescriptions[0].dateStart) {
        this.localPrescriptions[0].dateStart = new Date().toISOString().split('T')[0];
      }
      delete this.localPrescriptions[0].dateEnd;
      this.updateSubsequentDates(0);
    },
    updateSubsequentDates(changedIndex) {
      if (this.localPrescriptions.length <= 1) return;

      let currentDate;
      if (changedIndex === 0) {
        currentDate = new Date(this.localPrescriptions[0].dateStart);
      } else {
        currentDate = new Date(this.localPrescriptions[changedIndex].dateEnd);
      }

      for (let i = Math.max(1, changedIndex + 1); i < this.localPrescriptions.length; i++) {
        currentDate.setDate(currentDate.getDate() + 90);
        this.localPrescriptions[i].dateEnd = currentDate.toISOString().split('T')[0];
        delete this.localPrescriptions[i].dateStart;
      }
    },
    calculateNextDate() {
      if (this.localPrescriptions.length === 0) {
        return new Date().toISOString().split('T')[0];
      }
      const lastPrescription = this.localPrescriptions[this.localPrescriptions.length - 1];
      const lastDate = new Date(lastPrescription.dateEnd || this.localPrescriptions[0].dateStart);
      lastDate.setDate(lastDate.getDate() + this.sleepPrescriptionDayStep);
      return lastDate.toISOString().split('T')[0];
    },
    showNotification(message, type) {
      this.notification.message = message;
      this.notification.type = type;
      setTimeout(() => this.clearNotification(), 5000);
    },
    clearNotification() {
      this.notification.message = '';
      this.notification.type = '';
    },
    extractErrorMessage(info, message) {
      if (info && typeof info === 'object') {
        const errorKey = Object.keys(info).find(key => key.startsWith('error_message_') || key.includes('error'));
        if (errorKey && info[errorKey]) return info[errorKey];
      }
      return message || 'An error occurred. Please try again.';
    },
    checkForFailures(info) {
      return Object.entries(info).some(([key, value]) => {
        const isPaymentFailure = key.startsWith('payment_') &&
            typeof value === 'string' &&
            value.toLowerCase().includes('failed');
        const isPrescriptionIssue = key.startsWith('prescription_sent_') &&
            typeof value === 'string' &&
            value.toLowerCase().includes('no');
        return isPaymentFailure || isPrescriptionIssue;
      });
    }
  },
};
</script>

<style scoped>
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
}

.modal-content {
  position: relative;
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  width: 80%;
  max-width: 80vw;
  min-width: 300px;
  max-height: 90vh;
  overflow-y: auto;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-width: 95vw;
  }
}

.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  color: #333;
}

.close-button:hover {
  color: #f00;
}

.table-responsive {
  overflow-x: auto;
  max-width: 100%;
}

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
}

th, td {
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid #ddd;
  white-space: normal;
}

th {
  background-color: #f2f2f2;
  font-weight: bold;
}

input[type="text"],
input[type="number"],
input[type="date"],
select {
  width: 100%;
  min-width: 100px;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.product-select {
  min-width: 0;
  max-width: 100%;
}

.price-input {
  width: 100%;
  max-width: 80px;
}

.remove-button {
  padding: 6px 12px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.remove-button:hover {
  background-color: #c82333;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.center {
  text-align: center;
}

.right-align {
  text-align: right;
}

.modal-footer {
  margin-top: 20px;
  text-align: right;
}

.button-secondary--small {
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 10px;
}

.button-secondary--small:hover {
  background-color: #5a6268;
}

.button-primary--small:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  opacity: 0.7;
}

.add-prescription-button {
  position: absolute;
  top: 5%;
  right: 5%;
}

.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 15px 20px;
  border-radius: 4px;
  color: white;
  z-index: 1000;
  max-width: 300px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.notification.success {
  background-color: #28a745;
}

.notification.error {
  background-color: #dc3545;
}

.close-notification {
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  margin-left: 10px;
}

.close-notification:hover {
  color: #ddd;
}
</style>


// WEBPACK FOOTER //
// resources/assets/js/CreateSleepPrescriptions.vue